import React, { useEffect, useState } from 'react';
import AnalisisCasos from './AnalisisCasos';
import AnalisisComparar from './AnalisisComparar';
import AnalisisCorridas, { type SaltoACorrida } from './AnalisisCorridas';
import { fetchConfig } from './api';
import type { ConfigAnalisis, PlantillaCorrida } from './tipos';
import { Aviso, btnSecundario } from './ui';

type Sub = 'casos' | 'corridas' | 'comparar';
const SUBS: Array<{ id: Sub; label: string }> = [
  { id: 'casos', label: 'Casos' },
  { id: 'corridas', label: 'Corridas' },
  { id: 'comparar', label: 'Comparar' },
];

/**
 * Pestaña Análisis del test masivo (spec 2026-09-03-analisis-fidelidad-test-masivo-design):
 * sub-pestañas Casos · Corridas · Comparar. Carga la config del backend UNA vez y la
 * reparte. Las tres sub-vistas quedan montadas (se ocultan con `hidden`) para no perder
 * el dataset cargado y la selección al ir a mirar una corrida y volver.
 */
const AnalisisView: React.FC = () => {
  const [sub, setSub] = useState<Sub>('casos');
  const [config, setConfig] = useState<ConfigAnalisis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [salto, setSalto] = useState<SaltoACorrida | null>(null);
  // "Reutilizar configuración" de una corrida → Casos. `n` cambia en cada uso para que
  // reutilizar la MISMA corrida dos veces vuelva a aplicarse.
  const [plantilla, setPlantilla] = useState<{ plantilla: PlantillaCorrida; n: number } | null>(null);
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    setError(null);
    fetchConfig()
      .then(setConfig)
      .catch((e) => {
        console.error('[analisis] config falló:', e);
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [intento]);

  const irACorrida = (runId: string) => {
    setSalto((s) => ({ runId, n: (s?.n ?? 0) + 1 }));
    setSub('corridas');
  };
  const reutilizar = (p: PlantillaCorrida) => {
    setPlantilla((prev) => ({ plantilla: p, n: (prev?.n ?? 0) + 1 }));
    setSub('casos');
  };

  if (error) {
    return (
      <div className="space-y-3">
        <Aviso error={`No se pudo cargar la config del análisis: ${error}`} />
        <button type="button" onClick={() => setIntento((i) => i + 1)} className={btnSecundario}>
          Reintentar
        </button>
      </div>
    );
  }
  if (!config) return <p className="text-sm text-gray-500">Cargando config del análisis…</p>;

  return (
    <div className="space-y-4">
      <nav className="flex gap-2">
        {SUBS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSub(s.id)}
            className={`rounded-full py-1.5 px-3 text-xs font-medium transition-colors ${
              sub === s.id ? 'bg-brand-black text-white' : 'bg-white text-brand-black border border-gray-200 hover:border-gray-400'
            }`}
          >
            {s.label}
          </button>
        ))}
        <span className="ml-auto self-center text-xs text-gray-500">
          {config.modelos.length} modelos · {config.tipos_error.length} tipos de error en el catálogo
        </span>
      </nav>
      <div className={sub === 'casos' ? '' : 'hidden'}>
        <AnalisisCasos config={config} onIrACorrida={irACorrida} plantilla={plantilla} />
      </div>
      <div className={sub === 'corridas' ? '' : 'hidden'}>
        <AnalisisCorridas activa={sub === 'corridas'} config={config} salto={salto} onReutilizar={reutilizar} />
      </div>
      <div className={sub === 'comparar' ? '' : 'hidden'}>
        <AnalisisComparar activa={sub === 'comparar'} config={config} />
      </div>
    </div>
  );
};

export default AnalisisView;
